import styled from '@emotion/styled'
import { CSSProperties, useState } from 'react'

import { Dayjs } from '@bemlo/date'
import { ShiftHours } from '@bemlo/enums'

import { Color, theme } from '../../constants'

type ShiftHoursDesign = {
  ordinary: string
  onCall: string
  onCallLight: string
}

const shiftHoursDesign: Record<ShiftHours, ShiftHoursDesign> = {
  DAY: {
    ordinary: Color.BLUE,
    onCall: Color.MEDIUM_BLUE,
    onCallLight: Color.LIGHTER_BLUE,
  },
  EVENING: {
    ordinary: Color.RED,
    onCall: Color.MEDIUM_RED,
    onCallLight: Color.LIGHTER_RED,
  },
  LONG: {
    ordinary: Color.PURPLE,
    onCall: Color.MEDIUM_RED,
    onCallLight: Color.LIGHTER_RED,
  },
  NIGHT: {
    ordinary: Color.VIOLET,
    onCall: Color.DEEP_PURPLE,
    onCallLight: Color.LIGHT_PURPLE,
  },
}

const LabelWrapper = styled.div({
  position: 'relative',
  width: '100%',
})

const Container = styled.div<{
  length: number
  height: number
  rounded: boolean
  design: ShiftHoursDesign
  mt: number | undefined
}>(({ length, height, rounded, design, mt }) => ({
  position: 'relative', // Added this line
  display: 'grid',
  gridTemplateColumns: `repeat(${length}, 1fr)`,
  width: '100%',
  height,
  marginTop: mt,
  borderRadius: theme.border.radius.normal,
  border: theme.border.style.thin,
  borderColor: design.ordinary,
  overflow: 'hidden',
  ...(rounded && {
    div: {
      borderRadius: 10000,
      ':not(:last-child)': {
        marginRight: 2,
      },
    },

    border: 'none',
  }),
}))

const Segment = styled.div<{
  length: number
  design: ShiftHoursDesign
  background: 'filled' | 'light' | 'striped'
}>(({ length, design, background }) => ({
  gridColumn: `span ${length}`,
  background:
    background === 'filled'
      ? design.ordinary
      : background === 'light'
        ? design.onCallLight
        : `repeating-linear-gradient(
            -45deg,
            ${design.onCall} 0,
            ${design.onCall} 6px,
            ${design.onCallLight} 6px,
            ${design.onCallLight} 12px
          )`,
}))

const Badge = (props: {
  time: string
  y: 'over' | 'under'
  x?: 'left' | 'right'
  background: string
  left?: string
  show?: boolean
}) => {
  const positionStyles: CSSProperties = props.left
    ? {
        left: props.left,
        transform: 'translateX(-50%)',
      }
    : {
        [props.x || 'left']: '0',
      }

  return (
    <div
      className="label"
      style={{
        position: 'absolute',
        background: props.background,
        color: Color.WHITE,
        padding: '0.1rem 0.3rem',
        borderRadius: '0.4rem',
        fontSize: 12,
        zIndex: 1,
        [props.y === 'over' ? 'top' : 'bottom']: '-1.2rem', // Adjusted for better spacing
        ...(props.x === 'left' && { borderBottomLeftRadius: '0' }),
        ...(props.x === 'right' && { borderBottomRightRadius: '0' }),
        ...positionStyles,
      }}
    >
      {props.time}
    </div>
  )
}

type ShiftCrossSectionProps = {
  size?: 'sm' | 'md' | 'lg'
  shiftHours?: ShiftHours
  mt?: number
  labeled?: true | 'hover'
  startDateTime: Dayjs
  endDateTime: Dayjs
  onCallTime?: {
    startDateTime: Dayjs
    endDateTime: Dayjs
  } | null
}

export const ShiftCrossSection = ({
  mt,
  shiftHours,
  startDateTime,
  endDateTime,
  onCallTime,
  size,
  labeled,
}: ShiftCrossSectionProps) => {
  const design = shiftHoursDesign[shiftHours || ShiftHours.DAY]

  const [isHovered, setIsHovered] = useState(false)
  const height =
    size === 'sm'
      ? theme.spacing[1]
      : size === 'md'
        ? theme.spacing[2]
        : theme.spacing['3.5']

  const length = endDateTime.diff(startDateTime, 'minute')
  if (!onCallTime) {
    return (
      <Container
        design={design}
        length={length}
        height={height}
        rounded={size === 'sm'}
        mt={mt}
      >
        <Segment design={design} length={length} background="filled" />
      </Container>
    )
  }

  const onCallLength = onCallTime.endDateTime.diff(
    onCallTime.startDateTime,
    'minute',
  )
  const onCallStart = onCallTime.startDateTime.diff(startDateTime, 'minute')
  const onCallEnd = onCallTime.endDateTime.diff(startDateTime, 'minute')

  const firstSegmentLength = onCallStart
  const secondSegmentLength = onCallLength
  const thirdSegmentLength = length - onCallEnd

  const firstTransitionPosition = (onCallStart / length) * 100
  const secondTransitionPosition = (onCallEnd / length) * 100

  return (
    <LabelWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Container
        design={design}
        length={length}
        height={height}
        rounded={size === 'sm'}
        mt={mt}
      >
        {!!firstSegmentLength && (
          <Segment
            length={firstSegmentLength}
            design={design}
            background="filled"
          />
        )}
        <Segment
          length={secondSegmentLength}
          design={design}
          background={size === 'sm' ? 'light' : 'striped'}
        />
        {!!thirdSegmentLength && (
          <Segment
            length={thirdSegmentLength}
            design={design}
            background="filled"
          />
        )}
      </Container>
      {(labeled === true || (isHovered && labeled === 'hover')) && (
        <>
          <Badge
            time={startDateTime.format('HH:mm')}
            x="left"
            y="over"
            background={Color.BLUE}
          />
          {!!firstSegmentLength && (
            <Badge
              time={onCallTime.startDateTime.format('HH:mm')}
              y="under"
              left={`${firstTransitionPosition}%`}
              background={Color.MEDIUM_BLUE}
            />
          )}
          {!!thirdSegmentLength && (
            <Badge
              time={onCallTime.endDateTime.format('HH:mm')}
              y="under"
              left={`${secondTransitionPosition}%`}
              background={Color.MEDIUM_BLUE}
            />
          )}
          <Badge
            time={endDateTime.format('HH:mm')}
            x="right"
            y="over"
            background={Color.BLUE}
          />
        </>
      )}
    </LabelWrapper>
  )
}
